import { FileWarning, Plus } from "lucide-react";
import { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import Skeleton from "react-loading-skeleton";
import { Button } from "../../../../components/custom/custom-button";
import ReuseModal from "../../../../components/fragment/modal/ReuseModal";
import {
  deletePromotion,
  getPromotionList,
  pausePromotion,
  publishPromotion,
} from "../../../../redux/promotion";
import RootState, {
  PromotionItem,
  PromotionStatus,
} from "../../../../redux/type";
import { ALL_ROUTES } from "../../../../routes/AllRoutes";
import CampaignCard from "../components/CampaignCard";
import { EDIT_PROMOTION_ID_KEY } from "../EditCampaign";
import { VIEW_PROMOTION_ID_KEY } from "../PromotionDetails";

const PROMOTIONS_PER_PAGE = 100;

const STATUS_FILTER_OPTIONS: { label: string; value: PromotionStatus | "" }[] =
  [
    { label: "All Statuses", value: "" },
    { label: "Draft", value: "draft" },
    { label: "Active", value: "active" },
    { label: "Paused", value: "paused" },
    { label: "Expired", value: "expired" },
  ];

export default function Promotions() {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const { promotions, loading_promotions, loading_promotion_action } =
    useSelector((state: RootState) => state.promotion);
  const { user } = useSelector((state: RootState) => state.auth);
  const canCreate = !!user?.permissions?.includes("create_promotion");
  const canEdit = !!user?.permissions?.includes("edit_promotion");
  const canPublish = !!user?.permissions?.includes("publish_promotion");
  const canDelete = !!user?.permissions?.includes("delete_promotion");

  const [statusFilter, setStatusFilter] = useState<PromotionStatus | "">("");
  const [actioningId, setActioningId] = useState<string | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<PromotionItem | null>(null);

  const refetchPromotions = () => {
    dispatch(
      getPromotionList({
        status: statusFilter || undefined,
        per_page: PROMOTIONS_PER_PAGE,
      }) as any,
    );
  };

  useEffect(() => {
    refetchPromotions();
  }, [dispatch, statusFilter]);

  const promotionRows = (promotions?.data || []).filter(Boolean);

  const handlePublish = async (promotion: PromotionItem) => {
    setActioningId(promotion.id);
    const result: any = await dispatch(publishPromotion(promotion.id) as any);
    setActioningId(null);
    if (result?.payload?.data?.status) refetchPromotions();
  };

  const handleUnpublish = async (promotion: PromotionItem) => {
    setActioningId(promotion.id);
    const result: any = await dispatch(pausePromotion(promotion.id) as any);
    setActioningId(null);
    if (result?.payload?.data?.status) refetchPromotions();
  };

  const handleView = (promotion: PromotionItem) => {
    localStorage.setItem(VIEW_PROMOTION_ID_KEY, promotion.id);
    navigate(ALL_ROUTES?.DASHBOARD_REFERRAL_VIEW_CAMPAIGN);
  };

  const handleEdit = (promotion: PromotionItem) => {
    localStorage.setItem(EDIT_PROMOTION_ID_KEY, promotion.id);
    navigate(ALL_ROUTES?.DASHBOARD_REFERRAL_EDIT_CAMPAIGN);
  };

  const handleConfirmDelete = async () => {
    if (!deleteTarget) return;
    const result: any = await dispatch(deletePromotion(deleteTarget.id) as any);
    if (result?.payload?.data?.status) {
      setDeleteTarget(null);
      refetchPromotions();
    }
  };

  return (
    <div className="py-[16px] space-y-[20px]">
      <div className="flex items-start justify-between">
        <div>
          <p className="text-[20px] font-bold text-[#1E1E1E]">Promotions</p>
          <p className="text-[14px] text-[#758089] mt-[4px]">
            Create and manage temporary referral campaigns
          </p>
        </div>
        <div className="flex items-center gap-[12px]">
          <select
            value={statusFilter}
            onChange={(e) =>
              setStatusFilter(e.target.value as PromotionStatus | "")
            }
            disabled={loading_promotions}
            className="h-[40px] rounded-[8px] border border-[#E4E4E7] bg-white px-[12px] text-[14px] text-[#3F3F46] outline-none cursor-pointer disabled:cursor-not-allowed disabled:opacity-60"
          >
            {STATUS_FILTER_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
          {canCreate && (
            <Button
              onClick={() =>
                navigate(ALL_ROUTES?.DASHBOARD_REFERRAL_CREATE_CAMPAIGN)
              }
            >
              <Plus className="size-[16px]" />
              New Campaign
            </Button>
          )}
        </div>
      </div>

      {loading_promotions ? (
        <div className="space-y-[16px]">
          <Skeleton height={140} />
          <Skeleton height={140} />
        </div>
      ) : promotionRows.length === 0 ? (
        <div className="flex flex-col items-center justify-center py-[80px]">
          <FileWarning className="size-[40px] text-[#D4D4D8]" />
          <p className="mt-[12px] text-[14px] text-[#A1A1AA]">
            No promotion found
          </p>
        </div>
      ) : (
        <div className="space-y-[16px]">
          {promotionRows.map((promotion) => (
            <CampaignCard
              key={promotion.id}
              promotion={promotion}
              onView={handleView}
              onEdit={handleEdit}
              onPublish={handlePublish}
              onUnpublish={handleUnpublish}
              onDelete={setDeleteTarget}
              disableActions={actioningId === promotion.id}
              canEdit={canEdit}
              canPublish={canPublish}
              canDelete={canDelete}
            />
          ))}
        </div>
      )}

      <ReuseModal
        visible={!!deleteTarget}
        onClose={() => setDeleteTarget(null)}
        onBtnClick={handleConfirmDelete}
        loading={loading_promotion_action}
        deleteType
        btnLabel="Delete"
        title="Delete Campaign"
        deleteText={`Are you sure you want to delete "${deleteTarget?.name}"? This action cannot be undone.`}
      />
    </div>
  );
}
